import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Input, Space, Spin, notification } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import OtpInput from "react-otp-input";
import { useTranslation } from "react-i18next";
import authService from "services/authService";
import { saveUser } from "utilities/authSession";
import RouteEnum from "constants/RouteEnum";

const MfaRecovery = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [recovery, setRecovery] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("mfaRecovery")) || null;
    } catch {
      return null;
    }
  });
  const [otp, setOtp] = useState("");
  const [qr, setQr] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (recovery || !location.state?.loginChallengeId) return;
    setLoading(true);
    authService
      .requestMfaRecovery(location.state.loginChallengeId)
      .then((result) => {
        setRecovery(result);
        sessionStorage.setItem("mfaRecovery", JSON.stringify(result));
      })
      .catch(() => notification.error({ message: t("genericError") }))
      .finally(() => setLoading(false));
  }, [location.state, recovery, t]);

  useEffect(() => {
    if (!cooldown) return undefined;
    const timer = window.setInterval(
      () => setCooldown((value) => Math.max(0, value - 1)),
      1000
    );
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const verifyEmail = async () => {
    setLoading(true);
    try {
      await authService.verifyRecoveryEmail({
        recovery_id: recovery.recovery_id,
        recovery_session_token: recovery.recovery_session_token,
        otp,
      });
      setRecovery({ ...recovery, status: "pending_admin_approval" });
      setOtp("");
    } catch {
      notification.error({ message: t("invalidOrExpiredCode") });
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const status = await authService.recoveryStatus({
        recovery_id: recovery.recovery_id,
        recovery_session_token: recovery.recovery_session_token,
      });
      const next = { ...recovery, status: status.status };
      setRecovery(next);
      sessionStorage.setItem("mfaRecovery", JSON.stringify(next));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setLoading(true);
    try {
      await authService.resendRecoveryEmail({
        recovery_id: recovery.recovery_id,
        recovery_session_token: recovery.recovery_session_token,
      });
      setCooldown(60);
    } catch {
      notification.error({ message: t("resendUnavailable") });
    } finally {
      setLoading(false);
    }
  };

  const beginReset = async () => {
    setLoading(true);
    try {
      const result = await authService.beginMfaReset({
        recovery_id: recovery.recovery_id,
        recovery_session_token: recovery.recovery_session_token,
      });
      setQr(result.qrCodeImageUrl);
      setMfaChallenge(result.challenge_id);
      setRecovery({ ...recovery, status: "enrollment_pending" });
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setLoading(true);
    try {
      const result = await authService.verifyMfa({
        challenge_id: mfaChallenge,
        otp,
        remember_me: false,
      });
      saveUser(result.user, result.csrf_token);
      sessionStorage.removeItem("mfaRecovery");
      navigate(RouteEnum.Extractpdf, { replace: true });
    } catch {
      notification.error({ message: t("invalidOrExpiredCode") });
    } finally {
      setLoading(false);
    }
  };

  if (!recovery && !location.state?.loginChallengeId) {
    return <Alert type="error" message={t("recoveryMustStartAtLogin")} />;
  }

  return (
    <div className="login-layout d-flex align-items-center justify-content-center">
      <Card title={t("mfaRecovery")} style={{ width: 480, maxWidth: "94%" }}>
        <Spin spinning={loading}>
          {recovery?.status === "email_verification_pending" && (
            <>
              <p>{t("recoveryCodeSent", { email: recovery.email_hint })}</p>
              <Input
                value={otp}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
              />
              <Space className="mt-3">
                <Button type="primary" disabled={otp.length !== 6} onClick={verifyEmail}>
                  {t("verifyCode")}
                </Button>
                <Button disabled={cooldown > 0} onClick={resend}>
                  {cooldown ? `${t("resendOTP")} (${cooldown})` : t("resendOTP")}
                </Button>
              </Space>
            </>
          )}
          {recovery?.status === "pending_admin_approval" && (
            <>
              <Alert type="info" message={t("waitingForAdminApproval")} />
              <Button className="mt-3" onClick={refresh}>{t("refreshStatus")}</Button>
            </>
          )}
          {recovery?.status === "rejected" && <Alert type="error" message={t("recoveryRejected")} />}
          {recovery?.status === "approved" && (
            <Button type="primary" onClick={beginReset}>{t("setupNewAuthenticator")}</Button>
          )}
          {recovery?.status === "enrollment_pending" && qr && (
            <>
              <img src={qr} alt={t("qrCode")} className="d-block mx-auto my-3" />
              <OtpInput
                value={otp}
                onChange={(value) => setOtp(value.replace(/\D/g, ""))}
                numInputs={6}
                renderInput={(props) => <input {...props} />}
              />
              <Space className="mt-3">
                <Button type="primary" disabled={otp.length !== 6} onClick={finish}>
                  {t("Complete_login")}
                </Button>
              </Space>
            </>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default MfaRecovery;
